import { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Mail, Lock, User, Baby, Calendar } from "lucide-react";

const AuthModal = ({ onClose, onAuthSuccess, skipNavigate = false }) => {
  const { setUser, checkAuth } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    parentName: "",
    childName: "",
    childAge: "",
    confirmGuardian: false,
    acceptTerms: false
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/auth/login`,
        {
          email: formData.email,
          password: formData.password
        },
        { withCredentials: true }
      );

      setUser(response.data.user);
      toast.success("Welcome back!");
      onClose();
      if (onAuthSuccess) {
        onAuthSuccess();
      } else if (!skipNavigate) {
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailRegister = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (!formData.confirmGuardian) {
      toast.error("Please confirm you are the legal guardian");
      return;
    }

    if (!formData.acceptTerms) {
      toast.error("Please accept the Terms of Service and Privacy Policy");
      return;
    }

    if (!formData.childAge) {
      toast.error("Please select your child's age");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/auth/register`,
        {
          email: formData.email,
          password: formData.password,
          name: formData.parentName,
          parent_name: formData.parentName,
          child_name: formData.childName,
          child_age: parseInt(formData.childAge),
          is_guardian_confirmed: formData.confirmGuardian,
          terms_accepted: formData.acceptTerms
        },
        { withCredentials: true }
      );

      setUser(response.data.user);
      toast.success("Account created successfully!");
      onClose();
      if (onAuthSuccess) {
        onAuthSuccess();
      } else if (!skipNavigate) {
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = `${window.location.origin}/dashboard`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Handle Google OAuth callback
  useEffect(() => {
    const processGoogleAuth = async () => {
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');
        
        if (sessionId) {
          setLoading(true);
          try {
            const response = await axios.post(
              `${API}/auth/session`,
              { session_id: sessionId },
              { withCredentials: true }
            );
            
            setUser(response.data.user);
            toast.success("Welcome!");
            
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
            onClose();
          } catch (error) {
            toast.error("Google authentication failed");
          } finally {
            setLoading(false);
          }
        }
      }
    };

    processGoogleAuth();
  }, []);

  return (
    <div className="p-6">
      <div className="text-center mb-6">
        <div className="flex items-center justify-center gap-3 mb-3">
          <img 
            src="/images/robot-genie-logo.png" 
            alt="Kids Genie" 
            className="w-16 h-16 object-contain"
          />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">
          <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
            Kids Genie
          </span>
        </h2>
        <p className="text-xs font-medium text-gray-600 mb-3">AI & Coding Academy</p>
        <p className="text-sm text-gray-600">Sign in to start your learning journey</p>
      </div>

      <Tabs defaultValue="login" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
          <TabsTrigger value="register" data-testid="register-tab">Parent Registration</TabsTrigger>
        </TabsList>

        <TabsContent value="login">
          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div>
              <Label htmlFor="login-email">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="login-email"
                  data-testid="login-email-input"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your@email.com"
                  required
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="login-password">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="login-password"
                  data-testid="login-password-input"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="••••••••"
                  required
                  className="pl-10"
                />
              </div>
            </div>

            <Button
              data-testid="email-login-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-6"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or continue with</span>
            </div>
          </div>

          <Button
            data-testid="google-login-btn"
            type="button"
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full rounded-full py-6 border-2"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Sign in with Google
          </Button>
        </TabsContent>

        <TabsContent value="register">
          {/* Parent Registration Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-blue-800 font-medium flex items-center gap-2">
              <span>👨‍👩‍👧</span>
              Parents/Guardians only. Children aged 6-12 need a parent to register.
            </p>
          </div>

          <form onSubmit={handleEmailRegister} className="space-y-4">
            {/* Parent Information Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <User className="w-4 h-4" /> Parent/Guardian Information
              </h3>
              
              <div>
                <Label htmlFor="register-parent-name">Your Full Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="register-parent-name"
                    data-testid="register-parent-name-input"
                    type="text"
                    name="parentName"
                    value={formData.parentName}
                    onChange={handleInputChange}
                    placeholder="Parent's full name"
                    required
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="register-email">Your Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="register-email"
                    data-testid="register-email-input"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    required
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="register-password">Create Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="register-password"
                    data-testid="register-password-input"
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    className="pl-10"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
              </div>
            </div>

            {/* Child Information Section */}
            <div className="space-y-3 pt-2 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Baby className="w-4 h-4" /> Child Information
              </h3>
              
              <div>
                <Label htmlFor="register-child-name">Child's Name</Label>
                <div className="relative mt-1">
                  <Baby className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="register-child-name"
                    data-testid="register-child-name-input"
                    type="text"
                    name="childName"
                    value={formData.childName}
                    onChange={handleInputChange}
                    placeholder="Child's first name"
                    required
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="register-child-age">Child's Age</Label>
                <div className="relative mt-1">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <select
                    id="register-child-age"
                    data-testid="register-child-age-input"
                    name="childAge"
                    value={formData.childAge}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  >
                    <option value="">Select age</option>
                    <option value="6">6 years old</option>
                    <option value="7">7 years old</option>
                    <option value="8">8 years old</option>
                    <option value="9">9 years old</option>
                    <option value="10">10 years old</option>
                    <option value="11">11 years old</option>
                    <option value="12">12 years old</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Legal Confirmations */}
            <div className="space-y-3 pt-2 border-t border-gray-200">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="confirm-guardian"
                  name="confirmGuardian"
                  checked={formData.confirmGuardian}
                  onChange={handleInputChange}
                  className="mt-1 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  required
                />
                <label htmlFor="confirm-guardian" className="text-sm text-gray-700">
                  I confirm I am <strong>18+ years old</strong> and the <strong>legal parent/guardian</strong> of the child being registered.
                </label>
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="accept-terms"
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleInputChange}
                  className="mt-1 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  required
                />
                <label htmlFor="accept-terms" className="text-sm text-gray-700">
                  I agree to the <a href="/terms" className="text-indigo-600 hover:underline" target="_blank">Terms of Service</a> and <a href="/privacy" className="text-indigo-600 hover:underline" target="_blank">Privacy Policy</a>.
                </label>
              </div>
            </div>

            <Button
              data-testid="email-register-btn"
              type="submit"
              disabled={loading || !formData.confirmGuardian || !formData.acceptTerms}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-6 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Creating account..." : "Create Parent Account"}
            </Button>
          </form>

          <p className="text-xs text-center text-gray-500 mt-4">
            By registering, you agree to our child safety and data protection policies in compliance with COPPA and GDPR-K.
          </p>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AuthModal;
